import type ItineraryPackage from './ItineraryPackage';
import type BillHandler from './BillHandler';
import type PassengerForm from './PassengerForm';
import { type VerifyWarningMessage } from './types';
import { type ExtraField } from '@clients/FlightApi';
import { DiscountType, type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';

export default class VerifyResult {
    /**
     * disable check pax type in selecting passengers
     *
     * @memberof VerifyResult
     */
    disablePaxTypeValidation = false;

    /**
     * The ID of reservation
     *
     * @type {number | null}
     * @memberof VerifyResult
     */
    reserveId: number | null = null;

    /**
     * The code of the bill
     *
     * @type {string}
     * @memberof VerifyResult
     */
    billCode = '';

    mac = '';
    /**
     * The remainig time for paying the bill
     *
     * @type {number}
     * @memberof VerifyResult
     */
    remainingTime = 0;

    /**
     * The payable price of the bill
     *
     * @type {number}
     * @memberof VerifyResult
     */
    payablePrice = 0;

    /**
     * The total price of the bill
     *
     * @type {number}
     * @memberof VerifyResult
     */
    totalPrice = 0;

    isFloatingReserve = false;

    /**
     * The price entries of the bill
     *
     * @type {InvoiceEntry[]}
     * @memberof VerifyResult
     */
    priceEntries: {
        itineraryPackage?: ItineraryPackage;
        prices: InvoiceEntry[];
    }[] = [];

    /**
     * The discount of the bill
     *
     * @type {number}
     * @memberof VerifyResult
     */
    discount = 0;

    /**
     * type of discount
     *
     * @type {DiscountType}
     * @memberof VerifyResult
     */
    discountType = DiscountType.Special;

    paxList: PassengerForm[] = [];

    adultForm: PassengerForm | null = null;

    childForm: PassengerForm | null = null;

    infantForm: PassengerForm | null = null;

    /**
     * The bill cancellation terms and conditions
     *
     * @type {string}
     * @memberof VerifyResult
     */
    cancellationTerms = '';

    /**
     * The ID of the bill
     *
     * @type {number}
     * @memberof VerifyResult
     */
    billId = 0;

    /**
     * Indicates whether the bill needs a secondary mobile number, or not
     *
     * @type {number}
     * @memberof VerifyResult
     */
    needsDestinationMobile = false;

    /**
     * The text of the warning message
     *
     * @type {VerifyWarningMessage[]}
     * @memberof VerifyResult
     */
    warningMessages: VerifyWarningMessage[] = [];

    /**
     * The text that will be a not for a service
     *
     * @type {VerifyWarningMessage[]}
     * @memberof VerifyResult
     */
    reserveNotes: string[] = [];

    /**
     * Indicates whether to load the default passanger or not
     *
     * @type {boolean}
     * @memberof VerifyResult
     */
    loadDefaultPassenger = false;

    /**
     * Updated departure time of the itinerary
     *
     * @type {boolean}
     * @memberof VerifyResult
     */
    departureTime?: Date;

    /**
     * Handlers for handling reserves inside the bill
     */
    billHandlers: BillHandler[] = [];

    extraInfoFields: ExtraField[] = [];

    maxCapacity = 0;

    haveCustomDiscount = false;

    public constructor(billHandler: BillHandler, init?: Partial<VerifyResult>) {
        Object.assign(this, init);
        this.billHandlers.push(billHandler);
    }

    // get defaultAdultFields(): PassengerFields {
    //     return this.adultForm?.defaultFields ?? PassengerFields.None;
    // }

    // get defaultChildFields(): PassengerFields {
    //     return this.childForm?.defaultFields ?? PassengerFields.None;
    // }

    // get defaultInfantFields(): PassengerFields {
    //     return this.infantForm?.defaultFields ?? PassengerFields.None;
    // }

    // get defaultIranianExtraFields(): PassengerFields {
    //     return this.adultForm?.iranianExtraFields ?? PassengerFields.None;
    // }

    // get defaultForeignerExtraFields(): PassengerFields {
    //     return this.adultForm?.foreignerExtraFields ?? PassengerFields.None;
    // }

    // get defaultAdultBuyExtraFields(): BuyExtraFields {
    //     return this.adultForm?.buyExtraFields ?? BuyExtraFields.None;
    // }

    // get defaultChildBuyExtraFields(): BuyExtraFields {
    //     return this.childForm?.buyExtraFields ?? BuyExtraFields.None;
    // }

    // get defaultInfantBuyExtraFields(): BuyExtraFields {
    //     return this.infantForm?.buyExtraFields ?? BuyExtraFields.None;
    // }

    /**
     * Merges another VerifyResult into the current. Used when multiple verify operations is done for multiple trips (or trip packages)
     *
     * @memberof VerifyResult
     */

    setFormsPackage(pkg: ItineraryPackage) {
        this.paxList.forEach(p => {
            p.optionalServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
            p.freeServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
        });
        if (this.adultForm) {
            this.adultForm.optionalServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
            this.adultForm.freeServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
        }
        if (this.childForm) {
            this.childForm.optionalServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
            this.childForm.freeServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
        }
        if (this.infantForm) {
            this.infantForm.optionalServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
            this.infantForm.freeServiceList.forEach(s => {
                if (!s.itineraryPackage) {
                    s.itineraryPackage = pkg;
                }
            });
        }
    }

    public merge(other: VerifyResult) {
        if (other.reserveId) {
            this.reserveId = other.reserveId;
        }

        this.haveCustomDiscount =
            this.haveCustomDiscount || other.haveCustomDiscount;
        this.remainingTime += other.remainingTime;
        this.payablePrice += other.payablePrice;
        this.totalPrice += other.totalPrice;

        this.priceEntries = [...this.priceEntries, ...other.priceEntries];

        this.discount += other.discount;
        this.needsDestinationMobile =
            this.needsDestinationMobile || other.needsDestinationMobile;
        this.warningMessages.push(...other.warningMessages);
        const newReserveNotes = other.reserveNotes.reduce(
            (prev, curr) =>
                !this.reserveNotes.includes(curr) ? [...prev, curr] : prev,
            [] as string[]
        );
        this.reserveNotes = [...this.reserveNotes, ...newReserveNotes];
        const mergedHandlers = [...this.billHandlers, ...other.billHandlers];
        const shouldNotMergeHandlers = mergedHandlers.every(
            handler =>
                handler.isSingleForRoundTrip &&
                handler.concreteType === mergedHandlers[0]?.concreteType
        );
        if (!shouldNotMergeHandlers) {
            this.billHandlers = mergedHandlers;
        }
        if (other.paxList.length) {
            const mergedPaxList: PassengerForm[] = [];
            Object.keys(PaxType)
                .filter(key => isNaN(Number(PaxType[key as any])))
                .forEach(key => {
                    const paxType = Number(key) as PaxType;
                    const paxList = this.paxList.filter(
                        px => px.type === paxType
                    );
                    const otherPaxList = other.paxList.filter(
                        px => px.type === paxType
                    );
                    if (paxList.length > otherPaxList.length) {
                        paxList.forEach((pax, i) => {
                            if (otherPaxList[i]) {
                                mergedPaxList.push(
                                    pax.mergeFields(otherPaxList[i])
                                );
                            } else if (otherPaxList[0]) {
                                mergedPaxList.push(
                                    pax.mergeFields(otherPaxList[0])
                                );
                            }
                        });
                    } else {
                        otherPaxList.forEach((pax, i) => {
                            if (paxList[i]) {
                                mergedPaxList.push(paxList[i].mergeFields(pax));
                            } else if (paxList[0]) {
                                mergedPaxList.push(pax.mergeFields(paxList[0]));
                            } else {
                                mergedPaxList.push(pax);
                            }
                        });
                    }
                });
            this.paxList = mergedPaxList;
        }
        if (this.adultForm && other.adultForm) {
            this.adultForm = this.adultForm.mergeFields(other.adultForm);
        } else {
            this.adultForm = this.adultForm || other.adultForm;
        }
        if (this.childForm && other.childForm) {
            this.childForm = this.childForm.mergeFields(other.childForm);
        } else {
            this.childForm = this.adultForm || other.childForm;
        }
        if (this.infantForm && other.infantForm) {
            this.infantForm = this.infantForm.mergeFields(other.infantForm);
        } else {
            this.infantForm = this.infantForm || other.infantForm;
        }
        this.extraInfoFields = new GeneralSet<ExtraField>()
            .setCompareKey('Label')
            .addAll(this.extraInfoFields)
            .addAll(other.extraInfoFields)
            .toArray();
        this.disablePaxTypeValidation =
            this.disablePaxTypeValidation && other.disablePaxTypeValidation;
        this.isFloatingReserve =
            this.isFloatingReserve || other.isFloatingReserve;
    }
}
